import { css } from 'aphrodite';
import { parseAsStringLiteral, useQueryState } from 'nuqs';
import React from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { useClientSiteDailyAnalyticsQuery } from '../../../../queries/Website/useClientSiteQuery';
import { Button } from '../../../components-v2/Button';
import { Pill } from '../../../components-v2/Pill';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components-v2/Select';
import { Skeleton } from '../../../components-v2/Skeleton';
import { THEME } from '../../../components-v2/Theme/theme';
import { AnalysisIcon } from '../../../components/svgs/icons/AnalysisIcon';
import { InfoIcon } from '../../../components/svgs/icons/InfoIcon';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/Tooltip';
import { styleSheet as s } from './styles';
import {
	buildMetricTiles,
	DEFAULT_RANGE,
	formatShortDate,
	hasRollups,
	MetricTile,
	RANGE_OPTIONS,
	rangeEndingYesterday,
	topItems,
	trafficByDay,
} from './metricsReport';

const PAGE_VIEWS_COLOR = THEME.colors.chart.levBlue;
const VISITS_COLOR = THEME.colors.chart.enchantment;
const CHART_AXIS_TICK = { fontSize: THEME.typography.fontSizes.xxs, fill: THEME.colors.text.main } as const;

const rangeParser = parseAsStringLiteral(RANGE_OPTIONS.map(option => option.value)).withDefault(DEFAULT_RANGE);

export function WebsiteMetrics({ onOpenEditor }: { onOpenEditor?: () => void }) {
	const [range, setRange] = useQueryState('range', rangeParser);
	const { from, to } = rangeEndingYesterday(Number(range));
	const reportQuery = useClientSiteDailyAnalyticsQuery({ from, to });
	// After a range change the previous range's report stays as placeholder data; it must never stand in for a
	// failed fetch, or the old numbers would sit under the new range's label.
	const report = reportQuery.isError ? undefined : reportQuery.data;
	const showingPreviousRange = reportQuery.isPlaceholderData;

	const toolbar = (
		<div className={css(s.toolbar)}>
			{report?.rollups.lastDate ? (
				<span className={css(s.updated)}>Updated nightly · through {formatShortDate(report.rollups.lastDate)}</span>
			) : null}
			<div className={css(s.rangeSelect)}>
				<Select
					items={RANGE_OPTIONS.map(option => ({ ...option }))}
					value={range}
					onValueChange={value => {
						const next = RANGE_OPTIONS.find(option => option.value === value);
						if (next) {
							setRange(next.value === DEFAULT_RANGE ? null : next.value);
						}
					}}
				>
					<SelectTrigger styles={[s.rangeTrigger]} aria-label='Date range'>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{RANGE_OPTIONS.map(option => (
							<SelectItem key={option.value} value={option.value}>
								{option.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>
		</div>
	);

	if (reportQuery.isError) {
		return (
			<div className={css(s.root)}>
				{toolbar}
				<div className={css(s.empty)} role='alert'>
					<h2 className={css(s.emptyTitle)}>We couldn’t load your website’s traffic</h2>
					<p className={css(s.emptyBody)}>Refresh to try again, or contact us if it keeps happening.</p>
				</div>
			</div>
		);
	}

	if (!report) {
		return (
			<div className={css(s.root)} aria-busy='true'>
				{toolbar}
				<div className={css(s.tiles)}>
					{[0, 1, 2, 3].map(key => (
						<Skeleton key={key} styles={[s.skeletonTile]} />
					))}
				</div>
				<Skeleton styles={[s.skeletonChart]} />
			</div>
		);
	}

	if (!hasRollups(report)) {
		return (
			<div className={css(s.root)}>
				{toolbar}
				<NoVisitsYet onOpenEditor={onOpenEditor} />
			</div>
		);
	}

	const tiles = buildMetricTiles(report);
	const notTracked = tiles.filter(tile => tile.kind === 'notTracked');

	return (
		<div className={css(s.root, showingPreviousRange && s.stale)} aria-busy={showingPreviousRange}>
			{toolbar}
			{notTracked.length ? (
				<div className={css(s.banner)}>
					<InfoIcon fillColor={THEME.colors.info.dark} />
					<span className={css(s.bannerText)}>
						{listOf(notTracked.map(tile => tile.label))} start counting after you publish your site again.
					</span>
					{onOpenEditor ? (
						<Button variant='outline' size='sm' onClick={onOpenEditor}>
							Open the editor
						</Button>
					) : null}
				</div>
			) : null}
			<div className={css(s.tiles)}>
				{tiles.map(tile => (
					<MetricTileCard key={tile.key} tile={tile} />
				))}
			</div>
			<TrafficChart data={trafficByDay(report)} />
			<div className={css(s.breakdowns)}>
				<Breakdown
					title='Top pages'
					labelHeading='Page'
					countHeading='Page views'
					items={topItems(report, 'topPages')}
				/>
				<Breakdown
					title='Where visitors came from'
					labelHeading='Source'
					countHeading='Visits'
					items={topItems(report, 'topReferrers')}
				/>
			</div>
		</div>
	);
}

function listOf(labels: string[]) {
	const [first, ...rest] = labels;
	const all = [first, ...rest.map(label => label.toLowerCase())];
	return all.length > 1 ? `${all.slice(0, -1).join(', ')} and ${all[all.length - 1]}` : first;
}

function MetricTileCard({ tile }: { tile: MetricTile }) {
	return (
		<div className={css(s.tile)}>
			<div className={css(s.tileLabel)}>
				<span>{tile.label}</span>
				{tile.hint ? (
					<Tooltip>
						<TooltipTrigger className={css(s.hintTrigger)} aria-label={`About ${tile.label.toLowerCase()}`}>
							<InfoIcon fillColor={THEME.colors.text.light} />
						</TooltipTrigger>
						<TooltipContent>{tile.hint}</TooltipContent>
					</Tooltip>
				) : null}
			</div>
			{tile.kind === 'value' ? (
				<>
					<div className={css(s.tileValue)}>{tile.value}</div>
					{tile.caption ? <div className={css(s.tileCaption)}>{tile.caption}</div> : null}
				</>
			) : (
				<>
					<div className={css(s.tileValue, s.tileValueMuted)} aria-hidden='true'>
						—
					</div>
					<Pill variant='label' color='gray' label={tile.kind === 'notTracked' ? 'Not tracked yet' : 'Not available'} />
					{tile.kind === 'unavailable' ? (
						<div className={css(s.tileCaption)}>We can’t confirm tracking was on for these dates.</div>
					) : null}
				</>
			)}
		</div>
	);
}

type TrafficRow = ReturnType<typeof trafficByDay>[number];

function TrafficChart({ data }: { data: TrafficRow[] }) {
	return (
		<section className={css(s.card)}>
			<div className={css(s.cardHeader)}>
				<h2 className={css(s.heading)}>Traffic by day</h2>
				<div className={css(s.legend)}>
					<span className={css(s.legendItem)}>
						<span className={css(s.legendSwatch)} style={{ background: PAGE_VIEWS_COLOR }} />
						Page views
					</span>
					<span className={css(s.legendItem)}>
						<span className={css(s.legendSwatch)} style={{ background: VISITS_COLOR }} />
						Visits
					</span>
				</div>
			</div>
			<ResponsiveContainer width='100%' height={240}>
				<LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
					<CartesianGrid vertical={false} stroke={THEME.colors.border.light} />
					<XAxis dataKey='label' tick={CHART_AXIS_TICK} stroke={THEME.colors.border.main} minTickGap={24} />
					<YAxis allowDecimals={false} tick={CHART_AXIS_TICK} stroke={THEME.colors.border.main} width={40} />
					<ChartTooltip content={<TrafficTooltip />} />
					<Line type='monotone' dataKey='pageViews' stroke={PAGE_VIEWS_COLOR} strokeWidth={2} dot={false} />
					<Line type='monotone' dataKey='visits' stroke={VISITS_COLOR} strokeWidth={2} dot={false} />
				</LineChart>
			</ResponsiveContainer>
		</section>
	);
}

function TrafficTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: TrafficRow }> }) {
	const row = active ? payload?.[0]?.payload : undefined;
	if (!row) {
		return null;
	}
	return (
		<div className={css(s.tooltip)}>
			<span className={css(s.tooltipTitle)}>{row.label}</span>
			<span>{row.pageViews.toLocaleString('en-US')} page views</span>
			<span>{row.visits.toLocaleString('en-US')} visits</span>
		</div>
	);
}

function Breakdown({
	title,
	labelHeading,
	countHeading,
	items,
}: {
	title: string;
	labelHeading: string;
	countHeading: string;
	items: ReturnType<typeof topItems>;
}) {
	return (
		<section className={css(s.card)}>
			<h2 className={css(s.heading)}>{title}</h2>
			{items.length ? (
				<>
					<div className={css(s.breakdownHeader)} aria-hidden='true'>
						<span>{labelHeading}</span>
						<span>{countHeading}</span>
					</div>
					<ul className={css(s.breakdownList)}>
						{items.map(item => (
							<li key={item.label} className={css(s.breakdownRow)}>
								<div className={css(s.breakdownLine)}>
									<span className={css(s.breakdownLabel)} title={item.label}>
										{item.label}
									</span>
									<span className={css(s.breakdownCount)}>{item.count}</span>
								</div>
								<div className={css(s.bar)}>
									<div className={css(s.barFill)} style={{ width: `${Math.round(item.share * 100)}%` }} />
								</div>
							</li>
						))}
					</ul>
				</>
			) : (
				<span className={css(s.breakdownEmpty)}>Nothing to show for these dates.</span>
			)}
		</section>
	);
}

function NoVisitsYet({ onOpenEditor }: { onOpenEditor?: () => void }) {
	return (
		<div className={css(s.empty)}>
			<div className={css(s.emptyIcon)}>
				<AnalysisIcon fillColor={THEME.colors.primary.main} />
			</div>
			<h2 className={css(s.emptyTitle)}>No visits counted yet</h2>
			<p className={css(s.emptyBody)}>
				We add up your site’s visits once a night. If your site is live, your first numbers show here tomorrow morning.
			</p>
			{onOpenEditor ? (
				<>
					<p className={css(s.emptyNote)}>
						Not published yet? Publish your site from the editor and we’ll start counting.
					</p>
					<Button onClick={onOpenEditor}>Open the editor</Button>
				</>
			) : null}
		</div>
	);
}
